import {
	usageDailyQuery,
	usageRange,
	usageSearchSchema,
	usageSummaryQuery,
} from "@commerce/usage/meta";
import {
	UsageSettings,
	UsageSettingsSkeleton,
} from "@commerce/usage/UsageSettings";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/settings/usage",
)({
	validateSearch: usageSearchSchema,
	// The range toggle refetches on the client; the loader only serves the first paint.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const { days } = usageSearchSchema.parse(location.search);
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			await Promise.all([
				queryClient.ensureQueryData(usageSummaryQuery(organizationId)),
				queryClient.ensureQueryData(
					usageDailyQuery(organizationId, usageRange(days ?? 30)),
				),
			]);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Usage - ${config.appName}` }] }),
	component: UsagePage,
});

function UsagePage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();

	if (!isOrganizationAdmin) {
		return <AdminOnlyNotice what="usage" />;
	}

	return (
		<AsyncBoundary
			fallback={<UsageSettingsSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<UsageSettings organizationId={organizationId} />
		</AsyncBoundary>
	);
}
